<script lang="ts">
  // Editor section "Werk": client and activity (both can be created inline)
  // and the description.
  import '../../messages/entries';
  import { Combobox, Field, Textarea, type ComboOption } from '../../ui';
  import { data } from '../../stores/data.svelte';
  import { t } from '../../i18n.svelte';
  import { toast } from '../../toast.svelte';
  import { errorMessage } from '../../api';
  import { clientColor } from '../../format';
  import EditorSection from './EditorSection.svelte';
  import type { EntryForm } from './form.svelte';

  let { form }: { form: EntryForm } = $props();

  const clientOptions = $derived.by<ComboOption[]>(() =>
    data.clients
      .filter((c) => c.active !== false || c.id === form.clientId)
      .slice()
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((c) => ({ value: c.id, label: c.name, color: clientColor(c), sublabel: c.active === false ? t('entries.archived') : undefined }))
  );

  const activityOptions = $derived.by<ComboOption[]>(() =>
    data.activities
      .filter((a) => (a.active !== false && (a.client_id === null || a.client_id === form.clientId)) || a.id === form.activityId)
      .slice()
      .sort((a, b) => (a.client_id === null ? 1 : 0) - (b.client_id === null ? 1 : 0) || a.name.localeCompare(b.name))
      .map((a) => ({ value: a.id, label: a.name, sublabel: a.client_id === null ? t('entries.activity_global') : undefined }))
  );

  async function createClient(name: string) {
    try {
      const c = await data.createClient({ name });
      toast.success(t('entries.client_created', { name: c.name }));
      return c.id;
    } catch (err) {
      toast.error(errorMessage(err));
      return null;
    }
  }

  async function createActivity(name: string) {
    try {
      const a = await data.createActivity({ name, client_id: form.clientId, default_rate: null });
      return a.id;
    } catch (err) {
      toast.error(errorMessage(err));
      return null;
    }
  }
</script>

<EditorSection title={t('entries.section_what')}>
  <Field label={t('field.client')} for={form.id('client')} error={form.show('client')}>
    <Combobox
      id={form.id('client')}
      options={clientOptions}
      value={form.clientId}
      placeholder={t('entries.client_placeholder')}
      invalid={!!form.show('client')}
      allowCreate
      createLabel={(q) => t('entries.create_client', { name: q })}
      oncreate={createClient}
      onchange={(v) => form.setClient(v)}
      emptyLabel={t('entries.no_results')}
    />
  </Field>
  <Field label={t('field.activity')} for={form.id('activity')} optional optionalLabel={t('common.optional')}>
    <Combobox
      id={form.id('activity')}
      options={activityOptions}
      value={form.activityId}
      placeholder={t('entries.activity_placeholder')}
      clearable
      allowCreate
      createLabel={(q) => t('entries.create_activity', { name: q })}
      oncreate={createActivity}
      onchange={(v) => form.setActivity(v)}
      emptyLabel={t('entries.no_activities')}
    />
  </Field>
  <Field label={t('field.description')} for={form.id('desc')} optional optionalLabel={t('common.optional')}>
    <Textarea
      id={form.id('desc')}
      rows={3}
      maxlength={500}
      showCount={form.description.length > 400}
      placeholder={t('entries.description_placeholder')}
      bind:value={form.description}
    />
  </Field>
</EditorSection>
